/*
  Mug & Melt product page.
  <mm-product> wraps the gallery and the purchase column. It owns its own
  listeners (removed in disconnectedCallback) so theme editor reloads, block
  reordering and re-renders never double-bind.
*/

const SELECTORS = {
  config: '[data-mm-config]',
  form: '[data-mm-form]',
  swatch: '[data-mm-swatch]',
  variantSelect: '[data-mm-variant-select]',
  tier: '[data-mm-tier]',
  mix: '[data-mm-mix]',
  candle: '[data-mm-candle]',
  candleInput: '[data-mm-candle-input]',
  add: '[data-mm-add]',
  buy: '[data-mm-buy]',
  addText: '[data-mm-add-text]',
  addArrow: '.mm-button__arrow',
  total: '[data-mm-total]',
  error: '[data-mm-error]',
  price: '[data-mm-price]',
  compare: '[data-mm-compare]',
  compareValue: '[data-mm-compare-value]',
  colorName: '[data-mm-color-name]',
  slide: '[data-mm-slide]',
  thumb: '[data-mm-thumb]',
};

class MmProduct extends HTMLElement {
  connectedCallback() {
    const configElement = this.querySelector(SELECTORS.config);
    this.form = this.querySelector(SELECTORS.form);
    if (!configElement || !this.form) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.config = JSON.parse(configElement.textContent);
    this.variants = new Map(this.config.variants.map((variant) => [String(variant.id), variant]));
    this.formatter = new Intl.NumberFormat(this.config.locale, {
      style: 'currency',
      currency: this.config.currency,
    });

    this.swatches = [...this.form.querySelectorAll(SELECTORS.swatch)];
    this.variantSelect = this.form.querySelector(SELECTORS.variantSelect);
    this.tiers = [...this.form.querySelectorAll(SELECTORS.tier)];
    this.mixElement = this.form.querySelector(SELECTORS.mix);
    this.candleRow = this.form.querySelector(SELECTORS.candle);
    this.candleInput = this.form.querySelector(SELECTORS.candleInput);
    this.addButton = this.form.querySelector(SELECTORS.add);
    this.buyButton = this.form.querySelector(SELECTORS.buy);
    this.addText = this.form.querySelector(SELECTORS.addText);
    this.addArrow = this.form.querySelector(SELECTORS.addArrow);
    this.totalElement = this.form.querySelector(SELECTORS.total);
    this.errorElement = this.form.querySelector(SELECTORS.error);
    this.slides = [...this.querySelectorAll(SELECTORS.slide)];
    this.thumbs = [...this.querySelectorAll(SELECTORS.thumb)];

    this.state = {
      variantId: this.readVariantId(),
      mugs: [],
      busy: false,
      submitter: null,
    };

    this.syncMugCount();
    this.renderMix();
    if (this.candleRow && this.config.candle) this.candleRow.hidden = false;

    this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
    this.form.addEventListener('click', (event) => this.onFormClick(event), { signal });
    for (const swatch of this.swatches) {
      swatch.addEventListener('change', () => this.onVariantChange(swatch.value), { signal });
    }
    this.variantSelect?.addEventListener('change', () => this.onVariantChange(this.variantSelect.value), { signal });
    for (const tier of this.tiers) {
      tier.addEventListener('change', () => this.onTierChange(), { signal });
    }
    this.candleInput?.addEventListener('change', () => this.updateTotal(), { signal });
    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId), { signal });
    }
    document.addEventListener('click', (event) => this.onBundleTrigger(event), { signal });
    document.addEventListener('play', (event) => this.onVideoPlay(event), { capture: true, signal });
    this.pauseVideosForReducedMotion();

    this.updateAll();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  /* ---------- State helpers ---------- */

  readVariantId() {
    const checked = this.form.querySelector('input[name="id"]:checked');
    const field = checked || this.variantSelect || this.form.querySelector('input[name="id"]');
    return field ? String(field.value) : String(this.config.variants[0]?.id);
  }

  get activeTier() {
    return this.tiers.find((tier) => tier.checked) || null;
  }

  get quantity() {
    return this.activeTier ? Number(this.activeTier.dataset.quantity) || 1 : 1;
  }

  get discount() {
    return this.activeTier ? Number(this.activeTier.dataset.discount) || 0 : 0;
  }

  get candleSelected() {
    return Boolean(this.candleInput?.checked && this.config.candle);
  }

  /* One rule: a mug follows the selected swatch until the shopper picks its own color. */
  mugVariantId(index) {
    const mug = this.state.mugs[index];
    return mug && mug.touched && mug.variantId ? mug.variantId : this.state.variantId;
  }

  mugVariants() {
    return Array.from({ length: this.quantity }, (_, index) => this.variants.get(this.mugVariantId(index)));
  }

  syncMugCount() {
    const quantity = this.quantity;
    while (this.state.mugs.length < quantity) this.state.mugs.push({ variantId: null, touched: false });
    this.state.mugs.length = quantity;
  }

  /* ---------- Formatting and pricing ---------- */

  formatMoney(cents) {
    return this.formatter.format(cents / 100);
  }

  tierAmounts(unitPrice, quantity, discount) {
    const original = unitPrice * quantity;
    const total = Math.round((original * (100 - discount)) / 100);
    return { original, total, perUnit: Math.round(total / quantity) };
  }

  /* ---------- Events ---------- */

  onFormClick(event) {
    const button = event.target.closest('button[type="submit"]');
    if (button) this.state.submitter = button;
  }

  onVariantChange(variantId) {
    const variant = this.variants.get(String(variantId));
    if (!variant) return;
    this.state.variantId = String(variant.id);

    const colorName = this.querySelector(SELECTORS.colorName);
    if (colorName) colorName.textContent = variant.title;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    if (variant.mediaId) this.showMedia(String(variant.mediaId));
    this.syncMixValues();
    this.updateAll();
  }

  onTierChange() {
    this.syncMugCount();
    this.renderMix();
    this.updateAll();
  }

  onBundleTrigger(event) {
    const trigger = event.target.closest('[data-select-bundle]');
    if (!trigger) return;
    event.preventDefault();

    const tier = this.tiers.find((candidate) => candidate.dataset.quantity === trigger.dataset.selectBundle);
    if (tier && !tier.checked) {
      tier.checked = true;
      tier.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.closest('.mm-section')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  /* ---------- Editorial video ---------- */

  /* The video plays on a loop; it only stays still for visitors who ask for reduced motion. */
  onVideoPlay(event) {
    if (event.target.matches?.('[data-mm-video]') && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      event.target.pause();
    }
  }

  pauseVideosForReducedMotion() {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    for (const video of document.querySelectorAll('[data-mm-video]')) video.pause();
  }

  async onSubmit(event) {
    event.preventDefault();
    const submitter = event.submitter || this.state.submitter;
    this.state.submitter = null;
    const buyNow = submitter === this.buyButton;
    await this.addToCart(buyNow);
  }

  /* ---------- Gallery ---------- */

  showMedia(mediaId) {
    if (!this.slides.some((slide) => slide.dataset.mediaId === mediaId)) return;
    for (const slide of this.slides) slide.hidden = slide.dataset.mediaId !== mediaId;
    for (const thumb of this.thumbs) {
      if (thumb.dataset.mediaId === mediaId) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }
  }

  /* ---------- Mix colors ---------- */

  renderMix() {
    if (!this.mixElement) return;
    const quantity = this.quantity;
    this.mixElement.replaceChildren();
    this.mixElement.hidden = quantity < 2 || this.variants.size < 2;
    if (this.mixElement.hidden) return;

    for (let index = 0; index < quantity; index += 1) {
      const id = `${this.config.formId}-mug-${index}`;
      const label = document.createElement('label');
      label.className = 'mm-mix__item';
      label.htmlFor = id;
      label.append(this.config.strings.mug.replace('__n__', index + 1));

      const select = document.createElement('select');
      select.className = 'mm-select mm-mix__select';
      select.id = id;
      select.setAttribute('aria-label', this.config.strings.mugAria.replace('__n__', index + 1));
      for (const variant of this.variants.values()) {
        const option = new Option(
          variant.available ? variant.title : this.config.strings.optionSoldOut.replace('__v__', variant.title),
          variant.id
        );
        option.disabled = !variant.available;
        select.add(option);
      }
      select.value = this.mugVariantId(index);
      select.addEventListener(
        'change',
        () => {
          this.state.mugs[index] = { variantId: select.value, touched: true };
          this.updateAll();
        },
        { signal: this.abortController.signal }
      );
      label.append(select);
      this.mixElement.append(label);
    }
  }

  syncMixValues() {
    if (!this.mixElement) return;
    this.mixElement.querySelectorAll('select').forEach((select, index) => {
      select.value = this.mugVariantId(index);
    });
  }

  /* ---------- Rendering ---------- */

  updateAll() {
    this.updatePriceRow();
    this.updateTierCards();
    this.updateTotal();
    this.updateButtons();
  }

  updatePriceRow() {
    const variant = this.variants.get(this.state.variantId);
    if (!variant) return;
    const price = this.querySelector(SELECTORS.price);
    const compare = this.querySelector(SELECTORS.compare);
    const compareValue = this.querySelector(SELECTORS.compareValue);
    if (price) price.textContent = this.formatMoney(variant.price);
    if (compare && compareValue) {
      compare.hidden = !(variant.compareAt > variant.price);
      compareValue.textContent = this.formatMoney(variant.compareAt);
    }
  }

  updateTierCards() {
    const variant = this.variants.get(this.state.variantId);
    if (!variant) return;
    for (const tier of this.tiers) {
      const card = tier.closest('label');
      const amounts = this.tierAmounts(variant.price, Number(tier.dataset.quantity), Number(tier.dataset.discount));
      const total = card.querySelector('[data-mm-tier-total]');
      const original = card.querySelector('[data-mm-tier-original]');
      const perUnit = card.querySelector('[data-mm-tier-per]');
      if (total) total.textContent = this.formatMoney(amounts.total);
      if (original) original.textContent = this.formatMoney(amounts.original);
      if (perUnit) perUnit.textContent = this.config.strings.perMug.replace('__p__', this.formatMoney(amounts.perUnit));
    }
  }

  currentTotal() {
    const subtotal = this.mugVariants().reduce((sum, variant) => sum + (variant ? variant.price : 0), 0);
    const bundle = Math.round((subtotal * (100 - this.discount)) / 100);
    return bundle + (this.candleSelected ? this.config.candle.price : 0);
  }

  updateTotal() {
    if (this.totalElement) this.totalElement.textContent = this.formatMoney(this.currentTotal());
  }

  updateButtons() {
    const available = this.mugVariants().every((variant) => variant && variant.available);
    this.addButton.disabled = !available;
    if (this.buyButton) this.buyButton.disabled = !available;
    if (this.addText) this.addText.textContent = available ? this.config.strings.add : this.config.strings.soldOut;
    if (this.addArrow) this.addArrow.hidden = !available;
    if (this.totalElement) this.totalElement.hidden = !available;
  }

  /* ---------- Cart ---------- */

  buildItems() {
    const quantities = new Map();
    for (let index = 0; index < this.quantity; index += 1) {
      const id = this.mugVariantId(index);
      quantities.set(id, (quantities.get(id) || 0) + 1);
    }

    const bundleName = this.activeTier?.dataset.title;
    const items = [...quantities].map(([id, quantity]) => {
      const item = { id: Number(id), quantity };
      if (bundleName) item.properties = { _bundle: bundleName };
      return item;
    });
    if (this.candleSelected) items.push({ id: this.config.candle.id, quantity: 1 });
    return items;
  }

  setBusy(busy, button) {
    this.state.busy = busy;
    for (const control of [this.addButton, this.buyButton]) {
      if (!control) continue;
      control.setAttribute('aria-disabled', String(busy));
      control.toggleAttribute('data-loading', busy && control === button);
    }
    this.form.toggleAttribute('aria-busy', busy);
    if (this.addText) {
      this.addText.textContent = busy ? this.config.strings.adding : this.config.strings.add;
    }
    if (!busy) this.updateButtons();
  }

  showError(message) {
    if (this.errorElement) this.errorElement.textContent = message;
  }

  async addToCart(buyNow) {
    if (this.state.busy) return;
    this.showError('');
    const button = buyNow ? this.buyButton : this.addButton;
    this.setBusy(true, button);

    const drawer = document.querySelector('cart-drawer');
    const useDrawer = !buyNow && drawer && typeof drawer.renderContents === 'function';
    const body = { items: this.buildItems() };
    if (useDrawer) {
      body.sections = drawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      drawer.setActiveElement?.(document.activeElement);
    }

    try {
      const response = await fetch(this.config.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || !data || data.status) {
        this.showError((data && (data.description || data.message)) || this.config.strings.error);
        this.setBusy(false);
        return;
      }

      if (buyNow) {
        window.location.assign(this.config.checkoutUrl);
        return;
      }
      if (useDrawer) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
        this.setBusy(false);
        return;
      }
      window.location.assign(this.config.cartUrl);
    } catch (error) {
      this.showError(this.config.strings.error);
      this.setBusy(false);
    }
  }
}

if (!customElements.get('mm-product')) customElements.define('mm-product', MmProduct);
